<template>
  <div class="membership-page">
    <v-card flat class="page-card">
      <!-- Premium Header -->
      <v-row class="header-row" no-gutters align="center">
        <v-col cols="8">
          <div class="title-block">
            <h1 class="page-title">{{ PageTitle || "Membership" }}</h1>
            <p class="page-subtitle">{{ PageDescription }}</p>
          </div>
        </v-col>
        <v-col cols="4" class="text-right">
          <v-btn class="refresh-btn" small @click="refreshPageData">
            <v-icon left>mdi-refresh</v-icon> Refresh
          </v-btn>
        </v-col>
      </v-row>

      <v-card-text>
        <!-- Loading -->
        <div v-if="ProgessStart == 1" class="loading-wrap">
          <v-progress-circular
            indeterminate
            size="56"
            color="aqua"
          ></v-progress-circular>
          <div class="loading-text">Fetching members — please wait...</div>
        </div>

        <v-row class="note-row mb-4">
          <v-col cols="12">
            <v-alert
              dense
              border="left"
              colored-border
              type="info"
              elevation="0"
              class="report-note"
            >
              <strong>Note:</strong>
              <span class="note-text">
                If any column is missing in the report, please check the Excel
                file.
              </span>
            </v-alert>
          </v-col>
        </v-row>
        <!-- Filters -->
        <v-card class="filter-card" outlined>
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            id="createForm"
            class="pa-4"
          >
            <v-row dense>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="JciYearCodeOptions"
                  :rules="JciYearCodeRules"
                  v-model="JciYearCode"
                  label="Year"
                  clearable
                  outlined
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="YearHalfOptions"
                  :rules="YearHalfRules"
                  v-model="YearHalf"
                  label="Half"
                  clearable
                  outlined
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="ZoneCodeOptions"
                  :rules="ZoneCodeRules"
                  v-model="ZoneCode"
                  label="Zone"
                  clearable
                  outlined
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="LomCodeOptions"
                  :rules="LomCodeRules"
                  v-model="LomCode"
                  label="LOM"
                  clearable
                  outlined
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="MemberTypeOptions"
                  :rules="MemberTypeRules"
                  v-model="MemberTypeCode"
                  label="Member Type"
                  clearable
                  outlined
                />
              </v-col>

              <v-col cols="12" class="text-center mt-3">
                <v-btn
                  :disabled="!valid"
                  class="action-btn aqua--btn"
                  @click="searchForm"
                >
                  <v-icon left>mdi-magnify</v-icon> Search
                </v-btn>
                <v-btn
                  v-if="tableData1.length > 0"
                  class="action-btn gold--btn"
                  @click="deleteConfirmAlert"
                >
                  <v-icon left>mdi-delete</v-icon> Delete
                </v-btn>
                <v-btn
                  v-if="DownloadFlag"
                  class="action-btn seafoam--btn"
                  @click.prevent="
                    generateExcel(
                      tableData1,
                      ExcelFields,
                      ExcelFileName,
                      'My Worksheet'
                    )
                  "
                >
                  <v-icon left>mdi-file-excel</v-icon> Download
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card>

        <!-- Results -->
        <v-card class="results-card" outlined v-if="tableData1.length > 0">
          <v-row class="results-header" no-gutters align="center">
            <v-col cols="6">
              <h3 class="results-title">Members List</h3>
              <div class="results-sub">
                {{ TotalRecords || tableData1.length }} records found
              </div>
            </v-col>
            <v-col cols="6" class="text-right">
              <!-- keep any extra controls here -->
            </v-col>
          </v-row>

          <v-card-text class="pt-0">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              class="search-field"
            ></v-text-field>

            <!-- Medium row height data table -->
            <v-data-table
              class="membership-table"
              v-model="selected1"
              :show-select="tableOptions1.ShowSelectFlag"
              :search="search"
              :item-key="tableOptions1.ItemKey"
              :headers="tableColumns1"
              :items="tableData1"
              :items-per-page="PerPage"
              :footer-props="tableOptions1.FooterProps"
              :item-height="64"
            >
              <template v-slot:item.ActiveStatusTxt="{ item }">
                <v-chip
                  :color="getActiveStatusColor(item.ActiveStatusTxt)"
                  dark
                  small
                >
                  {{ item.ActiveStatusTxt }}
                </v-chip>
              </template>

              <template v-slot:item.MemberImagePath="{ item }">
                <v-avatar size="48">
                  <v-img :src="item.MemberImagePath" />
                </v-avatar>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      icon
                      size="25px"
                      color="primary"
                      @click="previewData(item, e)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-file-search
                    </v-icon>
                  </template>
                  <span> Preview </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      icon
                      size="25px"
                      color="warning"
                      @click="editAlert(item, e)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-file-document-edit
                    </v-icon>
                  </template>
                  <span> Edit </span>
                </v-tooltip>
                <v-tooltip bottom v-if="RoleId == 2">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      icon
                      size="25px"
                      color="purple"
                      @click="editMlDateAlert(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-calendar-clock
                    </v-icon>
                  </template>
                  <span> Edit ML Date </span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Pagination -->
            <div
              class="text-center mt-4"
              v-if="Pagination && Pagination.last_page > 1"
            >
              <v-pagination
                v-model="CurrentPage"
                :length="Pagination.last_page"
                :total-visible="7"
                @input="getDuesPaidMembers"
                color="navy"
              ></v-pagination>
            </div>
          </v-card-text>
        </v-card>

        <!-- Empty state -->
        <v-container v-else class="empty-wrap">
          <v-row justify="center">
            <v-col cols="12" md="6" class="text-center">
              <v-icon size="64" color="grey lighten-1"
                >mdi-account-group</v-icon
              >
              <h3 class="mt-4">No records found</h3>
              <p class="text-muted">Use filters above to get members</p>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- existing dialogs unchanged -->
    <v-dialog v-model="editRecordPrompt" max-width="85%">
      <member-edit
        v-if="editRecordPrompt"
        pageDescription="Edit Member Details"
        :editRecordPrompt="editRecordPrompt"
        @hideEditRecordPrompt="hideEditRecordPrompt"
        :recordIndex="recordToEdit"
      ></member-edit>
    </v-dialog>

    <v-dialog v-model="editDateRecordPrompt" persistent max-width="50%">
      <member-ml-date-edit
        v-if="editDateRecordPrompt"
        :editDateRecordPrompt="editDateRecordPrompt"
        @hideEditDateRecordPrompt="hideEditDateRecordPrompt"
        :recordData="selectedData"
      ></member-ml-date-edit>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import MemberEdit from "@/view/pages/members/MemberEdit.vue";
import MemberMlDateEdit from "@/view/pages/erp/members/MemberMlDateEdit.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    MemberEdit,
    MemberMlDateEdit,
  },
  data() {
    return {
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,

      SuccessMessageTxtFlag: 0,
      SuccessMessageTxt: "",

      ErrorMessageTxtFlag: 0,
      ErrorMessageTxt: "",

      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},

      SelectFlag: true,
      DeleteFlag: false,

      alert: {},

      valid: true,

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCode: "",
      JciYearCodeOptions: [],

      YearHalfRules: [(v) => !!v || "Half is required"],
      YearHalf: "",
      YearHalfOptions: [
        { value: "First", text: "First Half" },
        { value: "Second", text: "Second Half" },
      ],

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCode: "",
      ZoneCodeOptions: [],

      LomCodeRules: [],
      LomCode: "",
      LomCodeOptions: [],

      MemberTypeRules: [(v) => !!v || "Member Type is required"],
      MemberTypeCode: "",
      MemberTypeOptions: [
        { value: 1, text: "JCI Member" },
        { value: 9, text: "Junior JCI Member" },
        { value: 2, text: "Associate Members" },
      ],

      MemberNameRules: [(v) => !!v || "Name is required"],
      MemberEmailRules: [],
      MemberMobileNoRules: [],
      MemberAddressRules: [],
      MemberCityRules: [],

      totalActiveAwards: 0,
      totalPendingAwards: 0,

      lastDate: "15-JAN-2021 11:59 PM",

      RoleId: "",

      ResultFlag: 0,
      BtnShowFlag: 0,

      NewMembers: [],

      bgColor: "#778899",
      position: "top-right",
      fabActions: [
        /*
        {
          name: 'deleteBtn',
          icon: 'delete',
          color: 'red',
        },
        */
        {
          name: "previewBtn",
          icon: "search",
          color: "orange",
        },
      ],

      singleSelect: true,
      selected1: [],
      selected2: [],
      search: "",
      search2: "",
      loading: true,
      LoadingFlag: false,
      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      editDateRecordPrompt: false,

      recordToEdit: "",
      selectedData: {},

      PaymentPaidDate: "",
      menu2: false,

      dialog: false,
      dialogDelete: false,
      rows: [],
      tableData1: [],
      tableOptions1: [],
      tableColumns1: [],
      TotalMembers: 0,

      editedIndex: -1,
      editedItem: {
        name: "",
        calories: 0,
        fat: 0,
        carbs: 0,
        protein: 0,
      },
      defaultItem: {
        name: "",
        calories: 0,
        fat: 0,
        carbs: 0,
        protein: 0,
      },

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
    };
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Item" : "Edit Item";
    },
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        var roleId = this.$session.get("RoleId");
        roleId = roleId == (null || undefined) ? 0 : roleId;
        this.RoleId = roleId;
        console.log({ roleId });

        this.getJciYearCodeOptions();
      }
    },
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    rows: function () {
      console.log("watch rows");
      var rows = this.rows;
      var n1 = rows.length;
      var flag = n1 == 0 ? 0 : 1;
      console.log("n1=" + n1 + ", flag=" + flag);
      this.ResultFlag = flag;
      if (n1 > 0) {
        var filter1 = {
          ActiveStatusTxt: "Active",
        };
        console.log("filter1=" + JSON.stringify(filter1));
        var records1 = rows;
        records1 = records1.filter(function (item) {
          for (var key in filter1) {
            if (item[key] === undefined || item[key] != filter1[key])
              return false;
          }
          return true;
        });
        var n2 = records1.length;
        console.log("n2=" + n2 + ", records1=" + JSON.stringify(records1));

        var filter2 = {
          ActiveStatusTxt: "Pending",
        };
        console.log("filter2=" + JSON.stringify(filter2));
        var records2 = rows;
        records2 = records2.filter(function (item) {
          for (var key in filter2) {
            if (item[key] === undefined || item[key] != filter2[key])
              return false;
          }
          return true;
        });
        var n3 = records2.length;
        console.log("n3=" + n3 + ", records2=" + JSON.stringify(records2));

        this.totalActiveAwards = n2;
        this.totalPendingAwards = n3;
      }
    },
    selected1: function () {
      console.log("watch selected1");
      this.calculateTotalMembers();
    },
    selected2: function () {
      console.log("watch selected2");
      this.calculateTotalMembers();
    },
    NewMembers: function () {
      console.log("watch NewMembers");
      this.calculateTotalMembers();
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.LoadingFlag = false;
      this.ZoneCodeOptionsLoading = false;
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LoadingFlag = false;
      this.LomCodeOptionsLoading = false;
    },
    JciYearCode: function () {
      console.log("watch JciYearCode");
      this.ZoneCode = "";
      this.LomCode = "";
      this.ZoneCode = [];
      this.LomCodeOptions = [];
      this.resetMessageTxt();
      this.selected1 = [];
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
      this.getZoneCodeOptions();
    },
    YearHalf: function () {
      console.log("watch YearHalf");

      this.resetMessageTxt();
      this.selected1 = [];
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
    },
    ZoneCode: function () {
      console.log("watch ZoneCode");

      this.LomCode = "";
      this.LomCodeOptions = [];
      this.resetMessageTxt();
      this.selected1 = [];
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
      this.getLomCodeOptions();
    },
    LomCode: function () {
      console.log("watch LomCode");

      this.resetMessageTxt();
      this.selected1 = [];
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
    },
    MemberTypeCode: function () {
      console.log("watch MemberTypeCode");

      this.resetMessageTxt();
      this.selected1 = [];
      this.tableData1 = [];
      this.tableOptions1 = [];
      this.tableColumns1 = [];
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    rowSelected(tr) {
      console.log("rowSelected called");
      console.log("tr=" + JSON.stringify(tr));
    },
    calculateTotalMembers() {
      console.log("calculateTotalMembers called");
      var c1 = this.selected1.length;
      var c2 = this.selected2.length;
      var c3 = this.NewMembers.length;
      console.log("c1=" + c1 + ", c2=" + c2 + ", c3=" + c3);
      var TotalMembers = parseInt(c1) + parseInt(c2) + parseInt(c3);
      console.log("TotalMembers=" + TotalMembers);
      this.TotalMembers = TotalMembers;
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    resetMessageTxt() {
      console.log("resetMessageTxt called");
      this.ErrorMessageTxt = 0;
      this.ErrorMessageTxtFlag = "";
      this.SuccessMessageTxtFlag = 0;
      this.SuccessMessageTxt = "";
      this.totalActiveAwards = 0;
      this.totalPendingAwards = 0;
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var id = tr.MemberId;
        // var id = 1;
        console.log("id=" + id);
        // var url = "/lom-single";
        var url = "/member-individual";
        var params = {
          id: id,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    previewAlert(tr) {
      console.log("previewAlert called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.recordToEdit = 1;
        this.previewRecordPrompt = true;
        this.selected = tr.AwardsId;
      } else {
        var message = "Kindly select one record to preview";
        this.errorMessage(message);
      }
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.searchForm();
    },
    editAlert(tr) {
      console.log("editAlert called");
      console.log({ tr });
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var id = tr.MemberId;
        console.log({ id });
        this.recordToEdit = id;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    editMlDateAlert(tr) {
      console.log("editMlDateAlert called");
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.selectedData = tr;
        this.editDateRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    hideEditDateRecordPrompt(flag) {
      console.log("hideEditDateRecordPrompt called");
      this.editDateRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    deleteAlert(tr) {
      console.log("deleteAlert called");
      console.log("tr=" + JSON.stringify(tr));
    },
    addNewRow(tableId) {
      console.log("addNewRow called");
      console.log("tableId=" + tableId);
      if (tableId == 1) {
        var n1 = this.AllDesignation.length;
        console.log("n1=" + n1);
        if (n1 < 5) {
          this.AllDesignation.push({
            year: "",
            level: "",
            position: "",
          });
        }
      }

      if (tableId == 2) {
        var n1 = this.NewMembers.length;
        console.log("n1=" + n1);
        if (n1 < 30) {
          this.NewMembers.push({
            MemberName: "",
            MemberEmail: "",
            MemberMobileNo: "",
            MemberAddress: "",
            MemberCity: "",
          });
        }
      }
    },
    deleteRow(tableId, index, tr) {
      console.log("deleteRow called");
      console.log(
        "tableId=" + tableId + ", index=" + index + ", tr=" + JSON.stringify(tr)
      );
      if (index > -1) {
        if (tableId == 1) {
          this.AllDesignation.splice(index, 1);
        }
        if (tableId == 2) {
          this.NewMembers.splice(index, 1);
        }
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "membership_list",
        Action: "list_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.resetMessageTxt();
    },
    getJciYearCodeOptions() {
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        LomCode: 3,
        ZoneCode: 2,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getContributionTypeIdOptions() {
      console.log("getContributionTypeIdOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        var selectbox1_source = "ContributionTypeId";
        var selectbox1_destination = "ContributionTypeIdOptions";
        var selectbox1_url = "api/year-wise-contribution/type-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "Jci Year should not be empty";
        this.alert = {
          flag: true,
          color: "error",
          message: message,
        };
      }
    },
    getZoneCodeOptions() {
      this.ZoneCodeOptions = [];
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.alert = {
          flag: true,
          color: "error",
          message: message,
        };
      }
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      console.log("YearCode=" + YearCode + ", ZoneCode=" + ZoneCode);
      if (YearCode != "" && ZoneCode != "") {
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.alert = {
          flag: true,
          color: "error",
          message: message,
        };
      }
    },
    searchForm() {
      console.log("searchForm is called");
      this.resetMessageTxt();
      var result = this.$refs.form.validate();
      console.log("result=" + result);
      if (result) {
        this.searchResult();
      } else {
        this.ErrorMessageTxtFlag = 1;
        this.ErrorMessageTxt = "Kindy fill required fields and then submit";
        console.log("some errors occured");
      }
    },
    searchResult() {
      console.log("searchResult called");

      this.resetMessageTxt();

      var LomCode = this.LomCode;
      var ZoneCode = this.ZoneCode;
      var YearCode = this.JciYearCode;
      var YearHalf = this.YearHalf;
      var MemberTypeCode = this.MemberTypeCode;
      console.log(
        "ZoneCode" +
          ZoneCode +
          ", LomCode=" +
          LomCode +
          ", YearCode=" +
          YearCode +
          ", YearHalf=" +
          YearHalf +
          ", MemberTypeCode=" +
          MemberTypeCode
      );

      if (YearCode != "" && YearHalf != "" && MemberTypeCode != "") {
        this.getDuesPaidMembers();
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        if (YearHalf == "") {
          message += "Year half should not be empty. ";
        }
        if (MemberTypeCode == "") {
          message += "MemberType should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getDuesPaidMembers() {
      console.log("getDuesPaidMembers called");

      this.resetMessageTxt();
      this.selected1 = [];
      this.tableData1 = [];

      var LomCode = this.LomCode;
      var ZoneCode = this.ZoneCode;
      var YearCode = this.JciYearCode;
      var YearHalf = this.YearHalf;
      var MemberTypeCode = this.MemberTypeCode;
      console.log(
        "ZoneCode=" +
          ZoneCode +
          "LomCode=" +
          LomCode +
          ", YearCode=" +
          YearCode +
          ", YearHalf=" +
          YearHalf +
          ", MemberTypeCode=" +
          MemberTypeCode
      );

      if (
        ZoneCode != "" &&
        YearCode != "" &&
        YearHalf != "" &&
        MemberTypeCode != ""
      ) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/member-dues/member-lists";
        var upload = {
          UserInterface: 1,
          ZoneCode: ZoneCode,
          LomCode: LomCode,
          YearCode: YearCode,
          YearHalf: YearHalf,
          MemberType: MemberTypeCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var table_content = [];
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        // thisIns.pageLoadingOn()
        thisIns.ProgessStart = 1;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.ProgessStart = 0;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            table_content = response.data.table_content;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            var options = table_content.TableOptions;

            thisIns.tableColumns1 = table_content.TableHeader;
            thisIns.tableOptions1 = options;
            if (flag == 1) {
              thisIns.tableData1 = records;

              thisIns.DownloadFlag = options.DownloadFlag;
              thisIns.ExcelFileName = options.ExcelFileName;
              thisIns.ExcelFields = options.ExcelFields;
            } else {
              console.log("error=" + output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
          });
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        if (YearHalf == "") {
          message += "Year half should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    deleteConfirmAlert() {
      this.resetMessageTxt();

      var LomCode = this.LomCode;
      var YearCode = this.JciYearCode;
      var YearHalf = this.YearHalf;
      console.log(
        "LomCode=" +
          LomCode +
          ", YearCode=" +
          YearCode +
          ", YearHalf=" +
          YearHalf
      );

      var selected = this.selected1;
      var n1 = selected.length;
      console.log("n1=" + n1 + ", selected=" + JSON.stringify(selected));

      if (LomCode != "" && YearCode != "" && YearHalf != "" && n1 > 0) {
        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            var upload = {
              LomCode: LomCode,
              YearCode: YearCode,
              YearHalf: YearHalf,
              Members: selected,
            };
            this.deleteRecords(upload);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (LomCode == "") {
          message += "Lom should not be empty. ";
        }
        if (YearHalf == "") {
          message += "Year half should not be empty. ";
        }
        if (n1 == 0) {
          message += "Select the members. ";
        }
        this.toast("error", message);
      }
    },
    deleteRecords(upload) {
      console.log("deleteRecords called");

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/member-dues/destroy";
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var successTxt = "";
      var errorTxt = "";
      var flag = 0;

      // thisIns.pageLoadingOn()
      thisIns.ProgessStart = 1;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          // thisIns.pageLoadingOff()
          thisIns.ProgessStart = 0;

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);
          successTxt = response.data.success;
          errorTxt = response.data.error;
          console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

          if (flag == 1) {
            thisIns.tableData1 = [];
            thisIns.getDuesPaidMembers();
            thisIns.refreshPageData();
            thisIns.sweetAlert("success", output, true);
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
        });
    },
    addItem() {
      console.log("addItem called");
      this.dialog = true;
    },
    initialize() {
      this.rows = [];
    },

    editItem(tr) {
      this.editedIndex = this.rows.indexOf(tr);
      this.editedItem = Object.assign({}, tr);
      this.dialog = true;
    },

    deleteItem(tr) {
      this.editedIndex = this.rows.indexOf(tr);
      this.editedItem = Object.assign({}, tr);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.rows.splice(this.editedIndex, 1);
      this.closeDelete();
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    save() {
      if (this.editedIndex > -1) {
        Object.assign(this.rows[this.editedIndex], this.editedItem);
      } else {
        this.rows.push(this.editedItem);
      }
      this.close();
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
:root {
  --aqua: #0097d7;
  --navy: #3a67b1;
  --gold: #edbe38;
  --seafoam: #56bda3;
  --black90: #414042;
  --offblack: #0a0f29;
  --white: #ffffff;
}

.membership-page {
  .note-row {
    .report-note {
      background: rgba(58, 103, 177, 0.06);
      border-left: 4px solid var(--navy);
      color: var(--offblack);
      padding: 8px 12px;
    }
    .note-text {
      margin-left: 8px;
      color: var(--black90);
      font-size: 0.95rem;
    }
  }
  .page-card {
    background: var(--white);
    border-radius: 10px;
    box-shadow: 0 6px 20px rgba(10, 15, 41, 0.06);
    padding: 0;
  }

  .header-row {
    background: linear-gradient(90deg, var(--aqua), var(--navy));
    color: var(--white);
    padding: 20px 24px;
    border-radius: 10px 10px 0 0;
    margin-bottom: 12px;
  }

  .page-title {
    margin: 0;
    font-weight: 700;
    color: var(--white);
  }

  .page-subtitle {
    margin: 4px 0 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.95rem;
  }

  .filter-card {
    border-radius: 10px;
    margin-bottom: 18px;
    background: #fff;
  }

  .action-btn {
    text-transform: none;
    margin: 0 8px;
    padding: 10px 18px;
    color: #fff;
  }
  .aqua--btn {
    background: var(--aqua);
  }
  .navy--btn {
    background: var(--navy);
  }
  .gold--btn {
    background: var(--gold);
    color: var(--offblack);
  }
  .seafoam--btn {
    background: var(--seafoam);
    color: var(--offblack);
  }

  .results-card {
    border-radius: 10px;
    padding-bottom: 8px;
  }

  .results-header {
    padding: 18px;
    border-bottom: 1px solid #eee;
  }
  .results-title {
    margin: 0;
    color: var(--offblack);
    font-weight: 700;
  }
  .results-sub {
    color: var(--black90);
    font-size: 0.95rem;
  }

  .search-field {
    max-width: 420px;
  }

  /* Medium row height for data table */
  .membership-table .v-data-table__wrapper td,
  .membership-table .v-data-table__wrapper th {
    height: 64px !important;
    vertical-align: middle;
  }

  .membership-table .v-chip {
    font-weight: 600;
  }

  .loading-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36px 0;
    color: var(--black90);
  }

  .empty-wrap {
    padding: 36px 0;
  }
}

/* color utility classes if needed */
.aqua {
  background-color: var(--aqua) !important;
}
.navy {
  background-color: var(--navy) !important;
}
.gold {
  background-color: var(--gold) !important;
}
.seafoam {
  background-color: var(--seafoam) !important;
}
// ...existing code...
/* -- map Vuetify color="xxx" props and local utility classes to your palette -- */
.v-btn[color="aqua"],
.v-btn.aqua--btn {
  background-color: var(--aqua) !important;
  color: #fff !important;
  box-shadow: none !important;
}
.v-btn[color="navy"],
.v-btn.navy--btn {
  background-color: var(--navy) !important;
  color: #fff !important;
}
.v-btn[color="gold"],
.v-btn.gold--btn {
  background-color: var(--gold) !important;
  color: var(--offblack) !important;
}
.v-btn[color="seafoam"],
.v-btn.seafoam--btn {
  background-color: var(--seafoam) !important;
  color: var(--offblack) !important;
}

/* progress + avatars */
.v-progress-circular[color="aqua"] {
  color: var(--aqua) !important;
}
.v-avatar img {
  object-fit: cover;
}

/* pagination active item */
.v-pagination[color="navy"] .v-pagination__item--active,
.v-pagination .v-pagination__item--active.navy {
  background-color: var(--navy) !important;
  color: #fff !important;
  border-color: var(--navy) !important;
}

/* chips when you pass color names (slot uses getActiveStatusColor) */
.v-chip.bg-aqua {
  background-color: var(--aqua) !important;
  color: #fff !important;
}
.v-chip.bg-navy {
  background-color: var(--navy) !important;
  color: #fff !important;
}
.v-chip.bg-gold {
  background-color: var(--gold) !important;
  color: var(--offblack) !important;
}
.v-chip.bg-seafoam {
  background-color: var(--seafoam) !important;
  color: var(--offblack) !important;
}

/* Ensure utility classes work */
.aqua,
.aqua--btn {
  background-color: var(--aqua) !important;
  color: #fff !important;
}
.navy--btn .v-icon,
.navy--btn .v-icon * {
  color: #ffffff !important;
}
.gold--btn .v-icon,
.gold--btn .v-icon * {
  color: #0a0f29 !important;
}
.seafoam--btn .v-icon,
.seafoam--btn .v-icon * {
  color: #0a0f29 !important;
}

/* keep medium table rows */
.membership-table .v-data-table__wrapper td,
.membership-table .v-data-table__wrapper th {
  height: 64px !important;
  vertical-align: middle;
}
</style>
